import resolveModule from "@app/resolveModule/frontend";
import type { Router } from "@core/Api/Router";
import { useRouter } from "@core/Api/useRouter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { type CallApi, useApi } from "@core-ui/hooks/useApi";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { refreshPage } from "@core-ui/utils/initGlobalFuncs";
import { getGesSignInUrl } from "@ext/enterprise/components/SingInOut/utils/getGesSignInUrl";
import type { ClientWorkspaceConfig } from "@ext/workspace/WorkspaceConfig";
import { useCallback, useMemo } from "react";

type UseEnterpriseWorkspaceSwitchResult = {
	enterpriseWorkspace?: ClientWorkspaceConfig;
	switchWorkspace: CallApi<unknown>;
};

const useEnterpriseWorkspaceSwitch = (gesUrl: string): UseEnterpriseWorkspaceSwitchResult => {
	const workspaces = WorkspaceService.workspaces();
	const enterpriseWorkspace = useMemo(() => {
		return workspaces.find((item) => item.enterprise?.gesUrl === gesUrl);
	}, [workspaces, gesUrl]);

	const { call: switchWorkspace } = useApi({
		url: (api) => (enterpriseWorkspace ? api.switchWorkspace(enterpriseWorkspace.path) : null),
	});

	return { enterpriseWorkspace, switchWorkspace };
};

type UseEnterpriseSignInParams = {
	gesUrl: string;
	isWeb: boolean;
	isTauri: boolean;
	apiUrlCreator: ApiUrlCreator;
	router: Router;
};

export const useEnterpriseSignIn = ({ gesUrl, isWeb, isTauri, apiUrlCreator, router }: UseEnterpriseSignInParams) => {
	const { enterpriseWorkspace, switchWorkspace } = useEnterpriseWorkspaceSwitch(gesUrl);

	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	return useCallback(async () => {
		if (enterpriseWorkspace && isTauri) {
			try {
				await switchWorkspace();
				await refreshPage();
				return;
			} catch (error) {
				console.error("Failed to switch to enterprise workspace", error);
			}
		}

		const url = getGesSignInUrl(gesUrl, isWeb);
		if (isWeb) {
			window.location.replace(url);
			return;
		}

		await resolveModule("enterpriseLogin")(url, apiUrlCreator, router);
	}, [enterpriseWorkspace, switchWorkspace, gesUrl, isWeb, apiUrlCreator, router]);
};

const SignInEnterprise = ({ trigger }: { trigger: JSX.Element }) => {
	const router = useRouter();
	const { isWeb, isTauri } = usePlatform();
	const apiUrlCreator = ApiUrlCreatorService.value;
	const gesUrl = PageDataContextService.value.conf.activeGesUrl;

	const handleSignIn = useEnterpriseSignIn({
		gesUrl,
		isWeb,
		isTauri,
		apiUrlCreator,
		router,
	});

	return <div onClick={handleSignIn}>{trigger}</div>;
};

export default SignInEnterprise;
